/**
 * Connection Mutations Hooks
 *
 * React Query mutations for connection CRUD operations.
 * Provides loading states, error handling, and cache invalidation.
 */

import { useMutation, useQuery, useQueryClient, type QueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import {
  createConnection,
  deleteConnection,
  getConnection,
  listConnections,
  syncConnection,
  testConnection,
  updateConnection,
  getConnectionStatus,
  toAvailableConnection,
  getConnectorMetadata,
  getUniqueConnectionName,
  type CreateConnectionParams,
  type UpdateConnectionParams,
  type TestConnectionResult,
  type ConnectionResource,
  type ConnectorMetadata,
  type AppResourceContext,
} from '@/services/connectionApi';
import type { AvailableConnection, ConnectionReference, ConnectionStatus } from '@/types/connectionReference';
import { useAppStore } from '@/stores/appStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useAuth } from '@/auth/useAuth';
import { useCallback } from 'react';
import { telemetryService } from '@/services/telemetryService';
import { isAgentConnectionId, isManagedApiConnector, isServiceProviderConnector, removeConnectionReference } from '@/utils/connections';
import { generateConnectionName } from '@/services/manifestConnectionService';
import { fetchBuiltInConnectorMetadata } from '@/services/operationsService';
import { ConnectionsConfiguration } from '@/types';
import { saveConnectionsJson, saveDraftConnectionsJson } from '@/services/logicAppsApi';
import agentConnectorManifest from '@/data/manifests/agentLoopConnector';

// =============================================================================
// QUERY KEYS
// =============================================================================

export const connectionKeys = {
  all: ['connections'] as const,
  workflowConnections: (appId: string) => [...connectionKeys.all, 'workflow', appId] as const,
  lists: () => [...connectionKeys.all, 'list'] as const,
  list: (appId: string, connectorId?: string) =>
    [...connectionKeys.lists(), appId, connectorId?.toLowerCase()] as const,
  details: () => [...connectionKeys.all, 'detail'] as const,
  detail: (appId?: string, connectionName?: string) =>
    [...connectionKeys.details(), appId, connectionName] as const,
  status: (appId?: string, connectionName?: string) =>
    [...connectionKeys.all, 'status', appId, connectionName] as const,
};

export const connectorKeys = {
  all: ['connectors'] as const,
  metadata: (connectorId?: string) => [...connectorKeys.all, 'metadata', connectorId?.toLowerCase()] as const,
};

// =============================================================================
// CONTEXT HOOK
// =============================================================================

/**
 * Get resource context - returns function to build context with token
 */
export function useAppContextBuilder(autoAppId?: string): {
  buildContext: () => Promise<AppResourceContext | null>;
  appId: string;
} {
  const projectName = useAppStore((state) => state.selectedProject);
  const selectedApp = useAppStore((state) => state.selectedApp);
  const { getOttoToken } = useAuth();
  // If autoAppId is provided, use it directly. Otherwise, construct appId from selectedApp.
  // When selectedApp (or its required fields) is not yet loaded, return an empty string so
  // that downstream `!!appId` gates disable queries/mutations instead of firing against an
  // invalid `/automationProjects/undefined/applications/undefined` id.
  let appId: string;
  if (autoAppId) {
    appId = autoAppId;
  } else if (projectName && selectedApp?.name) {
    appId = `/automationProjects/${projectName}/applications/${selectedApp.name}`;
  } else {
    appId = '';
  }
  const buildContext = useCallback(async (): Promise<AppResourceContext | null> => {
    if (!appId) {
      return null;
    }
    const token = await getOttoToken();
    if (!token) {
      return null;
    }

    return {
      accessToken: token,
      projectName: projectName || '', // Use selected project from store, fallback to empty string if not available
      appId,
      location: 'brazilus' // TODO: Need to update location in the store when portal is loaded for a project.
    };
  }, [getOttoToken, appId, projectName]);

  return { buildContext, appId };
}

/**
 * Register a freshly-created connection with the workflow store and refresh
 * the picker list cache. Centralizes the two-line wiring that every
 * ConnectionCreationPanel host needs to perform after `onComplete` fires.
 */
export function useRegisterCreatedConnection(): (
  referenceKey: string,
  reference: ConnectionReference,
) => void {
  const addConnectionReference = useConnectionStore((state) => state.addConnectionReference);
  const queryClient = useQueryClient();
  return useCallback(
    (referenceKey, reference) => {
      addConnectionReference(referenceKey, reference);
      queryClient.invalidateQueries({ queryKey: connectionKeys.lists() });
    },
    [addConnectionReference, queryClient],
  );
}

// =============================================================================
// QUERY HOOKS
// =============================================================================

/**
 * List connections in the current app.
 * When autoAppId is omitted or empty, derives the appId from selectedApp in the store.
 */
export function useConnections(
  autoAppId?: string,
  connectorId?: string,
  enabled = true
): UseQueryResult<AvailableConnection[], Error> {
  const effectiveAppId = autoAppId || undefined;
  const { buildContext, appId } = useAppContextBuilder(effectiveAppId);

  return useQuery({
    queryKey: connectionKeys.list(appId, connectorId),
    queryFn: async () => {
      const ctx = await buildContext();
      if (!ctx) throw new Error('No context available');
      const resources = await listConnections(ctx, connectorId);
      return resources.map(toAvailableConnection);
    },
    enabled: enabled && !!appId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get a single connection by name
 */
export function useConnection(
  connectionName: string,
  enabled = true
): UseQueryResult<ConnectionResource, Error> {
  const { buildContext, appId } = useAppContextBuilder();

  return useQuery({
    queryKey: connectionKeys.detail(appId, connectionName),
    queryFn: async () => {
      const ctx = await buildContext();
      if (!ctx) throw new Error('No Azure context available');
      return getConnection(ctx, connectionName);
    },
    enabled: enabled && !!appId && !!connectionName,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get connection status
 */
export function useConnectionStatus(
  connectionName: string,
  enabled = true
): UseQueryResult<ConnectionStatus, Error> {
  const { buildContext, appId } = useAppContextBuilder();

  return useQuery({
    queryKey: connectionKeys.status(appId, connectionName),
    queryFn: async () => {
      const ctx = await buildContext();
      if (!ctx) throw new Error('No Azure context available');
      return getConnectionStatus(ctx, connectionName);
    },
    enabled: enabled && !!appId && !!connectionName,
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Poll every minute
  });
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

/**
 * Create a new connection
 */
export function useCreateConnection(): UseMutationResult<
  ConnectionResource,
  Error,
  CreateConnectionParams
> {
  const { buildContext, appId } = useAppContextBuilder();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateConnectionParams) => {
      const ctx = await buildContext();
      if (!ctx) throw new Error('No Azure context available');
      const created = await createConnection(ctx, params);
      return created;
    },
    onSuccess: (data, params) => {
      telemetryService.trackEvent('connection.created', {
        connectorName: params.connectorId,
      });

      // Invalidate connection lists to refetch
      queryClient.invalidateQueries({ queryKey: connectionKeys.lists() });

      // Add the new connection to cache
      if (appId) {
        queryClient.setQueryData(
          connectionKeys.detail(appId, data.name),
          data
        );
      }
    },
    onError: (error, params) => {
      telemetryService.trackEvent('connection.create_failed', {
        connectorName: params.connectorId,
        error: error.message,
      });
    },
  });
}

/**
 * Delete a connection
 */
export function useDeleteConnection(connectionsJson: ConnectionsConfiguration, draftConnectionsJson: ConnectionsConfiguration): UseMutationResult<void, Error, {connectionName: string, connectorId: string}> {
  const { buildContext, appId } = useAppContextBuilder();
  const projectName = useAppStore((state) => state.selectedProject) || '';
  const appName = useAppStore((state) => state.selectedApp?.name ?? '');
  const { getOttoToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({connectionName, connectorId}: {connectionName: string, connectorId: string}) => {
      const ctx = await buildContext();
      if (!ctx) throw new Error('No App context available');

      const isManagedApi = isManagedApiConnector(connectorId);
      let connectionId = `/connections/${connectionName}`;

      if (isManagedApi) {
        connectionId = `${ctx.appId}/connections/${connectionName}`;
        await deleteConnection(ctx, connectionName);
      }

      const newConnectionsJson = removeConnectionReference(connectionsJson, connectionId, connectorId);
      const newDraftConnectionsJson = removeConnectionReference(draftConnectionsJson, connectionId, connectorId);
      const context = { projectName, appName, getToken: getOttoToken };
      if (newConnectionsJson) {
        await saveConnectionsJson({ ...context, connectionsConfig: newConnectionsJson });

        queryClient.invalidateQueries({ queryKey: ['connections', projectName.toLowerCase(), appName.toLowerCase()] });
      }
      if (newDraftConnectionsJson) {
        await saveDraftConnectionsJson({ ...context, connectionsConfig: newDraftConnectionsJson });

        queryClient.invalidateQueries({ queryKey: ['connections-draft', projectName.toLowerCase(), appName.toLowerCase()] });
      }
    },
    onSuccess: (_data, { connectionName }) => {
      telemetryService.trackEvent('connection.deleted');

      // Remove from detail cache
      if (appId) {
        // Invalidate connection lists
        queryClient.invalidateQueries({ queryKey: connectionKeys.list(appId) });
        queryClient.removeQueries({
          queryKey: connectionKeys.detail(appId, connectionName),
        });
      }
    },
  });
}

/**
 * Sync a connection (refresh connection state from the provider)
 */
export function useSyncConnection(): UseMutationResult<void, Error, string> {
  const { buildContext, appId } = useAppContextBuilder();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (connectionName: string) => {
      const ctx = await buildContext();
      if (!ctx) throw new Error('No Azure context available');
      return syncConnection(ctx, connectionName);
    },
    onSuccess: (_data, connectionName) => {
      telemetryService.trackEvent('connection.synced');

      // Invalidate connection lists and detail cache
      queryClient.invalidateQueries({ queryKey: connectionKeys.lists() });

      if (appId) {
        queryClient.invalidateQueries({
          queryKey: connectionKeys.detail(appId, connectionName),
        });
      }
    },
  });
}

/**
 * Test a connection
 */
export function useTestConnection(): UseMutationResult<
  TestConnectionResult,
  Error,
  string
> {
  const { buildContext, appId } = useAppContextBuilder();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (connectionName: string) => {
      const ctx = await buildContext();
      if (!ctx) throw new Error('No Azure context available');
      return testConnection(ctx, connectionName);
    },
    onSuccess: (result, connectionName) => {
      telemetryService.trackEvent('connection.tested', {
        result: result.success ? 'success' : 'failure',
      });

      // Update the status cache
      if (appId) {
        queryClient.setQueryData(
          connectionKeys.status(appId, connectionName),
          result.status
        );
      }
    },
  });
}

/**
 * Update a connection
 */
export function useUpdateConnection(): UseMutationResult<
  ConnectionResource,
  Error,
  { connectionName: string; params: UpdateConnectionParams }
> {
  const { buildContext, appId } = useAppContextBuilder();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ connectionName, params }) => {
      const ctx = await buildContext();
      if (!ctx) throw new Error('No Azure context available');
      return updateConnection(ctx, connectionName, params);
    },
    onSuccess: (data, { connectionName }) => {
      // Update detail cache
      if (appId) {
        queryClient.setQueryData(
          connectionKeys.detail(appId, connectionName),
          data
        );
      }

      // Invalidate lists to show updated display name
      queryClient.invalidateQueries({ queryKey: connectionKeys.lists() });
    },
  });
}

// =============================================================================
// COMBINED HOOKS
// =============================================================================

/**
 * Hook for managing connection creation with automatic reference key generation
 */
export interface UseConnectionCreatorReturn {
  createConnection: (params: CreateConnectionParams) => Promise<ConnectionResource>;
  isCreating: boolean;
  error: Error | null;
  reset: () => void;
}

export function useConnectionCreator(): UseConnectionCreatorReturn {
  const mutation = useCreateConnection();

  return {
    createConnection: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}

/**
 * Hook for connection testing with result tracking
 */
export interface UseConnectionTesterReturn {
  testConnection: (connectionName: string) => Promise<TestConnectionResult>;
  isTesting: boolean;
  lastResult: TestConnectionResult | null;
  error: Error | null;
  reset: () => void;
}

export function useConnectionTester(): UseConnectionTesterReturn {
  const mutation = useTestConnection();

  return {
    testConnection: mutation.mutateAsync,
    isTesting: mutation.isPending,
    lastResult: mutation.data ?? null,
    error: mutation.error,
    reset: mutation.reset,
  };
}

/**
 * Hook for bulk connection status refresh
 */
export function useRefreshConnectionStatuses() {
  const queryClient = useQueryClient();
  const { appId } = useAppContextBuilder();

  return {
    refresh: async (connectionNames: string[]) => {
      if (!appId) return;

      await Promise.all(
        connectionNames.map((name) =>
          queryClient.invalidateQueries({
            queryKey: connectionKeys.status(appId, name),
          })
        )
      );
    },
    refreshAll: async () => {
      await queryClient.invalidateQueries({
        queryKey: connectionKeys.all,
      });
    },
  };
}

// =============================================================================
// CONNECTOR METADATA HOOK
// =============================================================================

/**
 * Fetch connector metadata including connection parameters
 * This is needed to determine OAuth requirements and render connection forms
 */
export function useConnectorMetadata(
  connectorId: string | undefined
): UseQueryResult<ConnectorMetadata | null, Error> {
  const { buildContext } = useAppContextBuilder();

  return useQuery({
    ...connectorMetadataQueryOptions(buildContext, connectorId),
    // Agent connectors look "local" but `queryFn` synthesizes their metadata
    // from the bundled manifest (displayName, iconUri, connectionParameterSets)
    // — running the query is how `ConnectorName` / `ConnectionCreationPanel`
    // get that data. Only true unfetchable connectors (built-in service
    // providers under `/connectionProviders/`) are gated out.
    enabled: !!connectorId && !isUnfetchableConnectorId(connectorId),
    retry: 1,
    retryDelay: 1000,
  });
}

/**
 * Get connector metadata from cache, fetching on demand if missing. Shares
 * the React Query key with `useConnectorMetadata` so the hook and imperative
 * callers always observe the same entry. Returns `null` for connectors with
 * no fetchable metadata endpoint (built-in / service provider connectors
 * under `/connectionProviders/`) and on fetch failure. Agent connectors are
 * synthesized from the bundled manifest by `queryFn`, so they flow through.
 */
export function ensureConnectorMetadata(
  queryClient: QueryClient,
  buildContext: () => Promise<AppResourceContext | null>,
  connectorId: string | undefined
): Promise<ConnectorMetadata | null> {
  if (!connectorId || isUnfetchableConnectorId(connectorId)) return Promise.resolve(null);
  return queryClient.fetchQuery(connectorMetadataQueryOptions(buildContext, connectorId));
}

function connectorMetadataQueryOptions(
  buildContext: () => Promise<AppResourceContext | null>,
  connectorId: string | undefined
) {
  return {
    queryKey: connectorKeys.metadata(connectorId),
    queryFn: async (): Promise<ConnectorMetadata | null> => {
      if (!connectorId) return null;

      const isAgent = !!connectorId && isAgentConnectionId(connectorId);
      // Local agent connector: synthesize ConnectorMetadata from the bundled
      // manifest so consumers (ConnectorName, ConnectionCreationPanel, etc.)
      // get displayName + iconUri AND the connectionParameterSets needed to
      // render the auth-method picker / parameter form. Without the parameter
      // sets, the template Create Connection panel renders no fields (it does
      // not pass a `connectorManifest` prop like the per-node ConnectionTab).
      if (isAgent) {
        const props = agentConnectorManifest.properties as {
          displayName?: string;
          iconUri?: string;
          brandColor?: string;
          connectionParameters?: Record<string, unknown>;
          connectionParameterSets?: {
            values?: Array<{
              name: string;
              uiDefinition?: { displayName?: string; description?: string };
              parameters?: Record<string, unknown>;
            }>;
          };
        };
        // The manifest stores parameter sets as { uiDefinition, values: [...] }
        // but ConnectorMetadata expects a flat array (matching the Azure API
        // shape used by getConnectorMetadata). Flatten to that shape.
        const parameterSets = props.connectionParameterSets?.values?.map((set) => ({
          name: set.name,
          uiDefinition: {
            displayName: set.uiDefinition?.displayName ?? set.name,
            ...(set.uiDefinition?.description ? { description: set.uiDefinition.description } : {}),
          },
          parameters: (set.parameters ?? {}) as Record<string, unknown>,
        }));
        return {
          id: agentConnectorManifest.id,
          name: agentConnectorManifest.name,
          displayName: props.displayName ?? agentConnectorManifest.name,
          iconUri: props.iconUri ?? '',
          brandColor: props.brandColor ?? '',
          ...(props.connectionParameters ? { connectionParameters: props.connectionParameters } : {}),
          ...(parameterSets && parameterSets.length > 0 ? { connectionParameterSets: parameterSets } : {}),
        };
      }

      try {
        const ctx = await buildContext();
        if (!ctx) return null;
        return await loadConnectorMetadata(ctx, connectorId);
      } catch (error) {
        console.warn('Failed to fetch connector metadata:', error);
        return null;
      }
    },
    staleTime: 5 * 60 * 1000,
  };
}

function isUnfetchableConnectorId(connectorId: string): boolean {
  // `/connectionProviders/` ids (built-in / service provider connectors) have
  // no managed-API metadata endpoint to fetch. Agent connectors are NOT in
  // this set — they are synthesized locally by `queryFn` from the manifest.
  return connectorId.includes('/connectionProviders/');
}

function loadConnectorMetadata(
  ctx: AppResourceContext,
  connectorId: string
): Promise<ConnectorMetadata | null> {
  return isServiceProviderConnector(connectorId)
    ? fetchBuiltInConnectorMetadata(ctx, connectorId.split('/').slice(-1)[0] as string)
    : getConnectorMetadata(ctx, connectorId);
}

// =============================================================================
// UNIQUE CONNECTION NAME HOOK
// =============================================================================

/**
 * Hook to get a unique connection name that's available both locally and in Azure API Hub.
 *
 * @returns Function to generate unique connection name
 */
export function useUniqueConnectionName(): {
  getUniqueName: (
    connectorId: string,
    connectorName: string,
    existingLocalNames: string[]
  ) => Promise<string>;
} {
  const { buildContext } = useAppContextBuilder();

  const getUniqueName = useCallback(
    async (
      connectorId: string,
      connectorName: string,
      existingLocalNames: string[]
    ): Promise<string> => {
      try {
        const ctx = await buildContext();
        if (!ctx) {
          // Fall back to local-only check if no context available
          return generateConnectionName(connectorName, existingLocalNames);
        }

        return await getUniqueConnectionName(ctx, connectorId, existingLocalNames, connectorName);
      } catch (error) {
        console.warn('Failed to check remote connection uniqueness, using local check:', error);
        return generateConnectionName(connectorName, existingLocalNames);
      }
    },
    [buildContext]
  );

  return { getUniqueName };
}